import { Avatar, Tooltip } from "antd";
import { useSelector } from "react-redux";
import { TagAvatar } from "@components/Avatars/TagAvatar";

interface TagBadgeProps {
  dateString: string;
  size?: number;
  popOutView?: boolean;
  stagedAssignTagIds?: string[];
  stagedRemoveTagIds?: string[];
}

const OVERLAP_RATIO = 0.4;
const OVERFLOW_TOOLTIP_SIZE = 20;
const MAX_VISIBLE = 2;

export const TagBadge = ({ dateString, size = 14, popOutView = false, stagedAssignTagIds, stagedRemoveTagIds }: TagBadgeProps) => {
  // Per-field selectors, not the whole team slice: this renders inside every
  // calendar cell, and the slice changes on all 55 of its actions. Keep these as
  // separate calls — one selector returning an object would allocate per call and
  // re-render more, not less.
  const calendarDayTags = useSelector(({ team }: any) => team.calendarDayTags);
  const calendarTags = useSelector(({ team }: any) => team.calendarTags);

  const entries: { assignment_id: string; tag_id: string }[] = calendarDayTags?.[dateString] ?? [];
  const removeSet = new Set(stagedRemoveTagIds ?? []);
  const baseTagIds = entries.map((e) => e.tag_id).filter((id) => !removeSet.has(id));
  const addedTagIds = (stagedAssignTagIds ?? []).filter((id) => !baseTagIds.includes(id));
  const tags = [...baseTagIds, ...addedTagIds].map((id) => calendarTags[id]).filter(Boolean);

  if (!tags.length) return null;

  const visible = tags.slice(0, MAX_VISIBLE);
  const overflowTags = tags.slice(MAX_VISIBLE);
  const overlap = Math.round(size * OVERLAP_RATIO);

  return (
    <div
      style={{
        ...(!popOutView && { position: "absolute", left: 0, right: 0 }),
        bottom: 4,
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        height: size,
      }}
    >
      {visible.map((tag, i) => (
        <TagAvatar
          key={tag.id}
          tag={tag}
          size={size}
          style={{ marginLeft: i > 0 ? -overlap : 0 }}
        />
      ))}
      {overflowTags.length > 0 && (
        <Tooltip
          title={
            <div style={{ display: "flex", gap: 6, padding: "2px 0" }}>
              {overflowTags.map((tag: any) => (
                <TagAvatar key={tag.id} tag={tag} size={OVERFLOW_TOOLTIP_SIZE} showTooltip={false} />
              ))}
            </div>
          }
        >
          <Avatar
            size={size}
            style={{
              marginLeft: -overlap,
              backgroundColor: "#d9d9d9",
              color: "#555",
              fontSize: size * 0.5,
              flexShrink: 0,
              cursor: "default",
            }}
          >
            +{overflowTags.length}
          </Avatar>
        </Tooltip>
      )}
    </div>
  );
};
